// EVERY WORKER REFUSAL WORD THE SETUP SURFACE CAN MEET, TURNED INTO A SENTENCE AN
// ADMIN CAN ACT ON. The wire codes are machine-facing and deliberately distinct so
// two problems can be told apart; not one of them is a sentence to show a person.
//
// ── THE VOCABULARY IS THE WORKER'S, TRANSCRIBED 2026-08-29 ─────────────────
// Every key below is a word one of the setup routes actually puts on the wire —
// the three gate refusals shared by all of them (`src/routes/setup.ts`
// `requireAdmin`/`requireAdminForBallot` and `src/grants.ts` `resolveViewer`), the
// roll refusals (`src/roll-load.ts` `RollLoadError` / `RollFreezeError`), and the
// lifecycle refusals (`ready`/`open`/`abandon`/`extend` in `src/routes/setup.ts`). A
// refusal with no copy is a blank message at the moment an Admin is trying to run a
// sitting, so the map is checked against the routes rather than grown by guesswork.
//
// ── THE EXTEND VOCABULARY WAS SHORT BY ONE WHEN IT WAS COUNTED ─────────────
// `POST …/ballot/:id/extend` was read off the route on 2026-09-01 and emits SEVEN
// words a screen can meet, not the six its `ExtendRefusal` union declares: the bad-body
// arm also raises `schedule_time_does_not_exist`, deliberately reusing `/schedule`'s
// word rather than coining a second one for the daylight-saving gap. Counting the union
// alone would have left that one word rendering the generic fallback. The lesson is the
// one this file already keeps — read the ROUTE, not the type that names most of it.
//
// ── EACH ONE ENDS BY SAYING NOTHING WAS CHANGED ────────────────────────────
// Every setup act is a mutation, and an Admin who was just refused one needs that
// fact before anything else — did it half-happen? So each sentence states it
// plainly, and the two that most need a human (`ambiguous_identity`, the empty
// House) end with what to do instead: a screen that can only say no teaches an
// operator to work around the fence.
//
// The `not_found` sentence names both a SITTING and a BALLOT because the setup
// routes are keyed on both — the roll and draft-list routes on the election id, the
// three lifecycle routes on the ballot id — so a single sentence has to cover
// either subject honestly. The Zoho-pull "not yet available" case is NOT here: it
// answers 404 and would otherwise borrow this sentence, so `doPull`
// (`SetupPage.tsx`) catches that one status on that one call before it reaches the
// map.
//
// ── THE ROLL WORDS ARE NOW A BACKSTOP, NOT THE FIRST ANSWER ────────────────
// Every roster refusal below is ALSO mirrored client-side, per line, by
// `setup/roster.ts`, which the roll panel runs over the pasted roster before it
// offers the load — the same relation `spec.ts`'s `validateDraft` has to the ballot
// words further down. So in the ordinary run an operator meets a sentence naming a
// LINE, and these are what remains for a wire surprise: a roster from the Zoho pull
// (P3b), a rule that has moved in the worker since `roster.ts` transcribed it, and
// the two the browser genuinely cannot pre-empt — `already_frozen` (another Admin
// froze the roll in the meantime) and `ambiguous_identity` raised against the
// addresses ALREADY on file rather than within the submitted roster.
//
// ── ONE WORD, TWO MEANINGS: THE ACT DECIDES THE SENTENCE ───────────────────
// `ambiguous_identity` is put on the wire by TWO different judgements, and they are
// not the same fault. `src/grants.ts` `resolveViewer` raises it at the GATE (403)
// when the address the operator SIGNED IN with maps to more than one `Contacts.id`.
// `src/roll-load.ts` raises it at INGEST (422) when a line in the SUBMITTED ROSTER
// gives an address a different `Contacts.id` from the one already on file for it.
// One sentence cannot be true of both: the registrar met the second on a live
// rehearsal, was told his sign-in was ambiguous, read it as a fault in his own
// account and stopped — when what he needed was the line to correct. So
// `refusalSentence` takes the ACT as well as the code, and `ACT_REFUSAL_COPY` holds
// the act's own words wherever the shared sentence would lie. The sign-in meaning
// stays the DEFAULT: it is what every other route means by the word.
//
// The rest of the map was re-read for the same shape on 2026-09-01 and no second
// case was found. `not_found` names a sitting OR a ballot deliberately (above) and
// is true of whichever act asked; `already_frozen` is raised by BOTH the load and
// the freeze and its sentence covers the two by name; `bad_request` is raised by
// five routes and claims nothing beyond "not understood". `ambiguous_identity` is
// the one word whose two raisings mean different things to the person reading it.
//
// **NOTHING WAS ADDED HERE FOR THE PASTED-ROSTER PATH, and that is a finding rather
// than an omission.** The map was re-checked key by key against `RollLoadError` and
// `RollFreezeError` (`src/roll-load.ts`) on 2026-09-01: all seventeen words already
// have a sentence. `roster.ts`'s own three refusals (`too_few_columns`,
// `header_columns_missing`, `contact_id_mangled`) are NOT wire codes and carry their
// prose with them, so adding them here would put half a vocabulary in each of two
// files — and inventing keys for codes no route sends is exactly the guesswork this
// header forbids.

/** The registrar's address, in one place so the escape hatch and any prose that
 *  names it cannot drift. */
export const REGISTRAR_EMAIL = 'registrar@confessinganglicans.nz';

const SETUP_REFUSAL_COPY: Record<string, string> = {
  // ── The three gates every setup route runs ────────────────────────────────
  unauthorized: 'Your session has ended. Sign in again — nothing was changed.',
  not_found: 'There is no sitting or ballot with that identifier. Nothing was changed.',
  no_grant: 'You do not hold a role in this sitting, so you cannot set it up. Nothing was changed.',
  not_administrator:
    'Setting up a sitting is the Synod Admin’s work. A Scrutineer may watch a sitting and read the counted sheets, but does not build the roll or the ballots. Nothing was changed.',
  unknown_identity:
    'The address you signed in with is not matched to anybody on this sitting. Nothing was changed — speak to the registrar, who can correct the record.',
  // THE SHARED PARISH MAILBOX. It says plainly what the fault is and what fixes it:
  // one address on two people would turn away BOTH of them at sign-in, and it is a
  // record to correct, not a decision about anybody.
  ambiguous_identity:
    'The address you signed in with points to more than one person — and that would lock BOTH of them out at sign-in. Nothing was changed. Give each their own address or Contacts ID, then try again.',

  // ── Loading the roll (POST …/roll) ────────────────────────────────────────
  bad_request: 'That request was not understood. Nothing was changed.',
  invalid_roster:
    'The roster did not arrive in a shape this platform can read. Nothing was changed.',
  no_electors: 'The roster was empty, and a roll needs at least one elector. Nothing was changed.',
  too_many_electors:
    'The roster carried more people than one roll load allows (500). Nothing was changed — if a synod is genuinely larger than that, tell the registrar.',
  contact_id_required:
    'Someone on the roster has no Contacts ID, and this platform seats people by ID and never by name alone. Nothing was changed — add the missing ID and try again.',
  duplicate_contact:
    'One Contacts ID appears twice on the roster, and each person is on the roll once. Nothing was changed — remove the repeat and try again.',
  email_required:
    'Someone on the roster has no email address, and a member signs in with theirs. Nothing was changed — add it and try again.',
  email_not_normalised:
    'One email address on the roster is not in plain lowercase, trimmed form. Nothing was changed — tidy it and try again.',
  email_malformed:
    'One email address on the roster is not a valid address. Nothing was changed — correct it and try again.',
  house_unknown:
    'Someone on the roster is in no known House — everyone is Clergy, Laity or the Bishop. Nothing was changed.',
  vote_flag_invalid:
    'Someone’s voting flag on the roster is neither yes nor no. Nothing was changed — set it and try again.',
  sandbox_roll_unsupported:
    'A sandbox sitting’s roll is practice data and cannot be built from a real roster of people. Nothing was changed.',
  already_frozen:
    'The roll for this sitting is already frozen — it cannot be loaded again, nor frozen twice. Nothing was changed. A change to a frozen roll is a formal amendment, which is a separate act.',

  // ── Freezing the roll (POST …/roll/freeze) ────────────────────────────────
  clergy_house_empty:
    'The roll cannot be frozen with no Clergy who may vote — a House with nobody in it is not a House. Nothing was changed. Load a roll that seats the Clergy first.',
  laity_house_empty:
    'The roll cannot be frozen with no Laity who may vote — a House with nobody in it is not a House. Nothing was changed. Load a roll that seats the Laity first.',
  too_many_bishops:
    'The roll names more than one Bishop, and a diocese has one Bishop or none. Nothing was changed — correct the roll and try again.',

  // ── The lifecycle (ready / open / abandon) ────────────────────────────────
  wrong_state:
    'This ballot is not at the step this action expected — it may already have moved on, or another Admin may be acting on it. Nothing was changed. Go back and open the setup again to pick it up where it is.',
  conflict:
    'Another change reached this ballot first. Nothing was changed. Go back and open the setup again.',
  roll_not_frozen:
    'The roll for this sitting has not been frozen yet, and a ballot is opened against the frozen roll. Nothing was changed — freeze the roll first.',

  // The quorum declaration (POST …/open). Each names its House so the operator
  // knows which figure to fix.
  quorum_declaration_required:
    'The quorum was not declared. Enter how many are present in each House, then try again. Nothing was changed.',
  quorum_clergy_invalid:
    'The Clergy figure is not a whole count of nought or more. Nothing was changed — correct it and try again.',
  quorum_laity_invalid:
    'The Laity figure is not a whole count of nought or more. Nothing was changed — correct it and try again.',
  quorum_bishop_invalid:
    'The Bishop figure must be 0 (not present) or 1 (present). Nothing was changed — correct it and try again.',

  // The abandon reason (POST …/abandon).
  reason_required:
    'A reason is needed to abandon a ballot, so the record says why. Nothing was changed — add one and try again.',
  reason_too_long:
    'That reason is longer than the record allows (500 characters). Nothing was changed — shorten it and try again.',

  // ── Building a ballot (POST …/ballot) ─────────────────────────────────────
  // Every word of `CreateBallotError` and `CreateBallotConflict`
  // (`src/ballot-create.ts`), transcribed 2026-08-30. Each ends "Nothing was
  // created." — the create-tense sibling of the "Nothing was changed." above, so an
  // operator refused a build knows a draft did NOT half-appear. The builder's own
  // `spec.ts` mirrors most of these client-side and refuses BEFORE the request, so
  // most are reached only by a wire surprise; the id-taken pair (409) and the class
  // that a mirror cannot pre-empt are the ones an operator meets in the ordinary run.
  invalid_spec:
    'That ballot could not be read in a shape this platform understands. Nothing was created.',
  title_required: 'The ballot needs a title. Nothing was created — add one and try again.',
  item_kind_unknown:
    'A ballot is either an election or a motion, and this one is neither. Nothing was created.',
  house_rule_unknown:
    'The counting rule for this ballot was not one this platform knows. Nothing was created.',
  secrecy_unknown:
    'A motion is either secret or attributable, and this one is neither. Nothing was created.',
  no_items:
    'A ballot needs at least one election or motion on it. Nothing was created — add one and try again.',
  item_kind_mismatch:
    'Every item on a ballot must be the same kind — all elections, or all motions. Nothing was created.',
  item_id_required: 'An item on the ballot was missing its identifier. Nothing was created.',
  item_id_duplicate:
    'Two items on the ballot share an identifier, and each must be its own. Nothing was created.',
  item_title_required:
    'An item on the ballot has no title. Nothing was created — name each election or motion and try again.',
  agenda_position_invalid:
    'An item on the ballot has no proper place in the running order. Nothing was created.',
  agenda_position_duplicate:
    'Two items on the ballot claim the same place in the running order. Nothing was created.',
  electorate_unknown:
    'One election does not say who may vote in it — the Clergy, the Laity, or both. Nothing was created.',
  seats_invalid:
    'An election must fill at least one place. Nothing was created — set the number of places and try again.',
  alternate_seats_invalid:
    'The number of alternate places cannot be fewer than nought. Nothing was created — correct it and try again.',
  method_unknown:
    'One election is counted by a method this platform cannot run. Nothing was created — choose first past the post or approval.',
  position_has_motion_columns:
    'An election carries settings that belong to a motion. Nothing was created.',
  threshold_kind_unknown:
    'A motion carries a majority setting this platform does not know. Nothing was created.',
  threshold_invalid:
    'A motion’s majority is not a whole fraction this platform can count. Nothing was created.',
  majority_base_unknown:
    'One motion does not say what its majority is measured against. Nothing was created — choose a majority and try again.',
  // The one refusal that joins a BALLOT-level setting to an ITEM-level one, so the
  // sentence names the setting to change rather than the motion: the motion's majority
  // is the ratified choice, and the counting rule is what has to follow it.
  house_rule_required_for_base:
    'One motion is measured against the members summoned to each House, and a ballot carrying it must be counted by separate chambers. Nothing was created — set how the votes are counted to Separate chambers and try again.',
  motion_class_unknown:
    'One motion is of a class this platform does not know. Nothing was created.',
  // The motion's SUBSTANCE. Unlike the value checks above it, this one is about a
  // field being empty rather than a value being unknown — the operative words are
  // what a member reads on the paper and consents to, so the sentence names them
  // rather than a column. Mirrored client-side in `spec.ts` `validateDraft`, so an
  // operator ordinarily meets the mirror and not this.
  instrument_required:
    'One motion does not carry the words Synod is being asked to agree. Nothing was created — enter the motion’s words in full and try again.',
  // The two OPTIONAL attributions, sent but not a usable name. Each sentence offers
  // BOTH ways out — a name, or nothing at all — because neither is required: a
  // motion is lawfully recorded without a mover, and copy that read as a demand
  // would push a registrar into inventing one to get past the screen.
  mover_invalid:
    'One motion carries a mover that is not a usable name. Nothing was created — enter the mover’s name, or leave it empty: a motion may be recorded without one.',
  seconder_invalid:
    'One motion carries a seconder that is not a usable name. Nothing was created — enter the seconder’s name, or leave it empty: a motion may be recorded without one.',
  motion_has_position_columns:
    'A motion carries settings that belong to an election. Nothing was created.',
  candidate_on_motion:
    'A motion cannot carry nominees — those belong to an election. Nothing was created.',
  candidate_id_required: 'A nominee on the ballot was missing an identifier. Nothing was created.',
  candidate_id_duplicate:
    'Two nominees on the ballot share an identifier, and each must be its own. Nothing was created.',
  candidate_display_name_required:
    'A nominee on the ballot has no name. Nothing was created — name each nominee and try again.',
  no_confidence_candidate:
    'No Confidence cannot be entered as a nominee — it is already a choice on every election, never a person. Nothing was created — remove it and try again.',
  candidate_item_unknown:
    'A nominee is attached to an election that is not on this ballot. Nothing was created.',
  candidate_contact_invalid:
    'A nominee’s linked record was not in a shape this platform can read. Nothing was created.',
  candidate_sandbox_has_contact:
    'A practice ballot’s nominee cannot be linked to a real person’s record. Nothing was created.',
  position_needs_candidate:
    'Every election must have at least one nominee (Standing Order 15.6). Nothing was created — add a nominee and try again.',
  // The two 409s — a caller id already spent by an earlier ballot. A retry mints a
  // fresh one, so the operator is told to try again rather than to change anything.
  item_id_taken:
    'One of this ballot’s identifiers is already in use by another ballot. Nothing was created — try again, and a fresh one will be minted.',
  candidate_id_taken:
    'One of this ballot’s nominee identifiers is already in use. Nothing was created — try again, and a fresh one will be minted.',

  // ── GIVING A RUNNING POLL MORE TIME (POST …/ballot/:id/extend) ───────────
  // Every word of `ExtendRefusal` (`src/routes/setup.ts`), transcribed 2026-09-01,
  // plus `schedule_time_does_not_exist`, which that route puts on the wire too — it
  // reuses `/schedule`'s word for the daylight-saving gap rather than coining a second
  // one for the same fault, so the sentence lives here now and is shared by both.
  // Seven words, not six: the route's own `ExtendRefusal` union names six, and the
  // seventh is emitted beside them from the bad-body arm.
  //
  // **EACH SENTENCE HERE IS THE FIGURE-FREE ONE, AND IT IS COMPLETE ON ITS OWN.**
  // The refusal bodies carry the figures a repair needs — the close that stands, the
  // latest instant that would be accepted, the cap in hours, the shape and an example
  // — and `ExtendStep.tsx` renders those in front of these sentences, because turning
  // an instant into "4:00pm on 19 September 2026" needs `wallClockInWords`, which
  // lives with the rules that govern it in `SchedulePanel.tsx`. This module stays free
  // of runtime imports (it is read by a test in the default node environment), so the
  // split is: the WORDS here, the FIGURES at the screen. A sentence must therefore
  // still be true and actionable when no figure arrives — an older worker, a body
  // that did not parse — which is why none of them ends mid-thought.
  //
  // A REFUSAL NEVER BLAMES. Extending is the enfranchising act on this surface: it
  // gives Synod more time to vote and can never take any away. So nothing below tells
  // the operator they did something wrong; each says what the poll's own schedule is
  // and what would be accepted instead.
  not_an_open_poll:
    'That ballot is not open, so there is no running poll to give more time to. Nothing was changed — only an open poll can be extended, and a ballot that has not opened has its window set on the ballot itself.',
  no_close_to_extend:
    'This poll has no closing time to move — it is running unbounded, and putting one in now would shorten it rather than extend it. Nothing was changed; the poll is still open, and a Scrutineer closes it when Synod has finished.',
  extend_invalid:
    'That closing time was not in a shape this platform can read. Nothing was changed — correct it and try again.',
  extension_too_far:
    'That closing time is further ahead than a single extension may carry a poll. Nothing was changed — choose an earlier time and try again. A poll may be extended more than once, so a long adjournment is several moves rather than one.',
  not_an_extension:
    'That closing time is not later than the one this poll already has, so it would give nobody more time to vote. Nothing was changed — choose a later time and try again.',
  extend_conflict:
    'Somebody else changed this poll’s closing time while you were setting yours, so nothing was changed. Open the setup again to see where it now stands, and extend it from there if it still needs longer.',
  // **NOT COLLAPSED INTO A SHAPE ERROR, AND THAT IS WHY THE WORKER SPENDS A SEPARATE
  // WORD ON IT.** The string was well-formed; what does not exist is the CLOCK TIME —
  // New Zealand skips 02:00–02:59 on the morning it enters daylight saving, so 2:30am
  // simply never happens that day. Told only "invalid", a registrar reads it as a
  // typing fault, retypes the same time and is refused again; told which hour vanished
  // and what to type instead, they fix it once. The sentence names the mechanism rather
  // than a hard-coded date, because the refusal does not say which bound or which year
  // tripped it. Raised by BOTH `/schedule` and `/extend`.
  schedule_time_does_not_exist:
    'One of those times does not exist on the date you gave it. New Zealand moves to daylight saving overnight on the last Sunday in September, and the clocks jump straight from 2:00am to 3:00am — so nothing in that hour ever happens. Nothing was changed. Choose a time before 2:00am or from 3:00am that morning, or another date, and try again.',

  // NOT a wire code — synthesised by a panel when the request reached no server at
  // all. Here rather than left to the fallback because "refused" would be a false
  // thing to say about a hall with bad wifi.
  unreachable:
    'The setup surface could not be reached. Nothing was changed — this is a problem with the connection, not with your standing. Try again in a moment.',
};

/**
 * WHICH ACT was refused, for the codes whose meaning depends on it (see the header).
 * A closed union rather than a free string, so a mistyped act cannot quietly fall
 * back to a sentence about something else — and {@link ACT_REFUSAL_COPY} is TOTAL
 * over it, so a new member cannot be added without its words being written.
 *
 * Only acts that need their OWN sentence for a code are named. Every other caller
 * passes nothing and gets the shared sentence: the default is the ordinary case, not
 * an omission.
 */
export type RefusalAct = 'roll-load';

/**
 * The sentences that REPLACE the shared one for a given act. Overrides only — a code
 * absent here falls through to {@link SETUP_REFUSAL_COPY}, so an act names just the
 * words it means differently and inherits the rest.
 */
const ACT_REFUSAL_COPY: Record<RefusalAct, Record<string, string>> = {
  // Loading the roll (POST …/roll) — the pasted roster and, from P3b, the Zoho pull.
  'roll-load': {
    // NOT the sign-in fault of the same name. The within-roster case (two lines, one
    // address, two ids) is caught per LINE by `roster.ts` before the request, so what
    // reaches here is the mapping already on file — the one thing the browser cannot
    // see. The remedy is offered both ways round because either is lawful: keep the
    // id the address already answers to, or give the person an address of their own.
    ambiguous_identity:
      'A line in the roster gives an address a different Contacts ID from the one already on file for it, and one address cannot stand for two people. Nothing was changed — use the Contacts ID already on file for that address, or give the person their own address, then try again.',
  },
};

/**
 * A wire code as a sentence, with a fallback that still tells the truth an Admin
 * needs — that nothing was written. The fallback names SETUP rather than the
 * portal, deliberately: the portal's name depends on who is reading (portal-name.ts),
 * and a sentence naming a different thing from the header above it reads as a fault.
 *
 * `act` is optional and names what was being attempted, for the codes whose meaning
 * turns on it. Omitting it is the ordinary case and returns the shared sentence.
 */
export function refusalSentence(code: string, act: RefusalAct | null = null): string {
  const forTheAct = act === null ? undefined : ACT_REFUSAL_COPY[act][code];
  return (
    forTheAct ??
    SETUP_REFUSAL_COPY[code] ??
    'The setup surface refused that, and nothing was changed.'
  );
}

/** Exposed for the test that pins the table against the routes' vocabulary. */
export { SETUP_REFUSAL_COPY };
